import React from "react";
import { WithTranslation, withTranslation } from "react-i18next";
import { filenameAssistantStore } from "./FilenameAssistantStore";
import { Loader } from "Components/General/Loader";
import { I18N_NS } from "Config/i18n";
import { observer } from "mobx-react";
import { MultiValuesPickerModal } from "./MultiValuesPickerModal";
import { Button } from "Components/General/Button";
import { CellValueEditorModal, ModalData } from "./CellValueEditorModal";
import { UploadedTmpFile } from "../FileUpload/FileUploadStore";
import { ButtonsRow } from "Components/Form/ButtonsRow";
import { ModalComponentProps } from "Components/General/Modal/ModalContext";
import { ConventionChecker } from "./ConventionChecker";
import { fileName, filetype } from "utils/filetype";
import { Jspreadsheet } from "Components/General/Jspreadsheet";
import jspreadsheet from "jspreadsheet";
import classNames from "classnames";
import { customDropdownEditor } from "./customDropdownEditor";
import { notificationError } from "utils/libHelpers/AntdNotification";
import { hasBlacklistedFilenameChars } from "utils/inputValidation";

export interface FilenameAssistantPdlFile extends API_PlanDeliveryListItem {
  id: number;
  name: string;
  type: string;
}

interface FilenameAssistantOverviewProps extends WithTranslation, ModalComponentProps {
  file?: UploadedTmpFile | API_UploadFile | FilenameAssistantPdlFile;
  onSubmit?: (file: UploadedTmpFile[]) => void;
  module?: "PDL" | "DOCS";
  files?: API_PlanDeliveryListItem[];
  onChange?: (values: API_PlanDeliveryListItem[], toDelete: API_PlanDeliveryListItem[]) => void;
  disabledAdd?: boolean;
  resetPlacehoders?: boolean;
  className?: string;
}

type TableData = {
  data: unknown[];
  style?: unknown[];
}[];

interface FilenameAssistantOverviewState {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  data: any;
  tableData: TableData;
  fileTypes: string[];
  fileNames: string[];
  fileAmounts: string[];
  fileDescriptions: string[];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  columns: { [x: string]: any }[];
  loading: boolean;
  conventionCase: "AND" | "OR";
  isPickerVisible: boolean;
  isMultiConvVisible: boolean;
  isFreeTextVisible: boolean;
  modalPickerData: {
    values: { name: string; description: string }[];
    initialValue: string;
    activePartConventions: API_FilenameAssistantConventionResponse[];
    additonalCharFilter: string[];
  };
  modalTextConvData: ModalData[];
  modalCell: HTMLTableCellElement;
  modalTitle: string;
  reinitialize: boolean;
  initialCellValue: string;
  markedAsDeletedRowIdxs: number[];
}

export const FilenameAssistantOverview = withTranslation(I18N_NS.PROJECT)(
  observer(
    class extends React.Component<FilenameAssistantOverviewProps, FilenameAssistantOverviewState> {
      private table = React.createRef<Jspreadsheet>();
      private numberOfRuleCol: number = 2;
      private noUpdateTable: boolean = false;
      static defaultProps: Partial<FilenameAssistantOverviewProps> = {
        module: "DOCS",
      };
      rowToFile: {} = {};
      blockExternChange: boolean = false;

      /**
       * Workaround for jspreadsheet stealing focus from modal inputs on first open.
       * Briefly closes and reopens the modal so jspreadsheet releases its event grip.
       */
      private rerenderModal = () => {
        requestAnimationFrame(() => {
          const { isPickerVisible, isMultiConvVisible, isFreeTextVisible } = this.state;

          this.setState({ isPickerVisible: false, isMultiConvVisible: false, isFreeTextVisible: false }, () => {
            requestAnimationFrame(() => {
              this.setState({ isPickerVisible, isMultiConvVisible, isFreeTextVisible });
            });
          });
        });
      };

      constructor(props: FilenameAssistantOverviewProps) {
        super(props);
        this.state = {
          data: null,
          tableData: [],
          fileTypes: [],
          fileNames: [],
          fileAmounts: [],
          fileDescriptions: [],
          columns: [],
          loading: false,
          isPickerVisible: false,
          isMultiConvVisible: false,
          conventionCase: null,
          isFreeTextVisible: false,
          modalPickerData: { values: [], initialValue: "", activePartConventions: null, additonalCharFilter: [] },
          modalTextConvData: [],
          modalCell: null,
          modalTitle: null,
          reinitialize: true,
          initialCellValue: null,
          markedAsDeletedRowIdxs: [],
        };
      }

      componentDidMount() {
        (async () => {
          if (this.isDocs) {
            this.numberOfRuleCol = 2;
            await filenameAssistantStore.fetch(this.props.file);
            this.parseData(this.props.file);
          } else {
            this.numberOfRuleCol = 4;
            await filenameAssistantStore.fetchPdlAssistant();
            if (!this.checkForDeletedRules()) {
              this.parseData();
            }
          }
        })();
      }

      componentDidUpdate() {
        if (this.props.resetPlacehoders) {
          this.noUpdateTable = true;
          this.blockExternChange = true;
          let hadReset = false;
          for (const rowIdx in this.rowToFile) {
            const rowData = this.table.current?.getRowData(rowIdx);
            if (rowData[3] === this.props.t("Enter a description")) {
              rowData[3] = "";
              hadReset = true;
              this.table.current?.setRowData(rowIdx, rowData);
            }
          }
          this.noUpdateTable = false;
          this.blockExternChange = false;
          if (hadReset) {
            this.table.current?.emitFakeUpdateTable();
          }
        }
      }

      checkForDeletedRules(): boolean {
        const unknownRules = {};
        for (const file of this.props.files) {
          if (!filenameAssistantStore.rules[file.ruleName]) {
            unknownRules[file.ruleName] = true;
          }
        }
        const keys = Object.keys(unknownRules);
        if (keys.length > 0) {
          filenameAssistantStore.setUnknownPDLRules(keys);
          return true;
        }
        return false;
      }

      contextMenu: jspreadsheet.Contextmenu = (instance, x, y) => {
        const items = [];
        if (!x || !y) {
          return items;
        }

        if (this.isPDL) {
          items.push({ type: "line" });
          items.push({
            title: this.props.t(this.state.markedAsDeletedRowIdxs.includes(+y) ? "Undelete" : "Delete"),

            onclick: () => {
              let current = this.state.markedAsDeletedRowIdxs;

              if (current.includes(+y)) {
                current = current.filter((row) => row !== +y);
              } else {
                current.push(+y);
              }
              this.setState({ markedAsDeletedRowIdxs: current }, () => {
                this.table.current?.emitFakeUpdateTable();
                this.handleExternChange();
              });
            },
          });
          items.push({ type: "line" });
        }
        return items;
      };

      /**
       * used to open all editors on single click and not on double click as it's default in jspreadsheet
       */
      onSelection: Jspreadsheet["onSelection"] = (instance, col1, row1, сol2, row2, event: MouseEvent) => {
        if (event?.button !== 0) {
          // otherwise editors also opens on right mouse click and then contextmenu is not working
          return;
        }
        const cell = this.table.current?.getCellFromCoords(col1, row1);
        const placeHolder = this.props.t("Enter a description");

        instance.openEditor(cell, this.isPDL && col1 === 3 && cell.textContent === placeHolder, event);
        window.setTimeout(() => {
          // this hack is needed, otherwise dropdown editors are opened, but directly closed again (after around 500ms)
          const container = document.querySelector<HTMLDivElement>(".jdropdown");
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          (container as any)?.dropdown?.close(true);
        });
        event?.preventDefault();
      };

      updateTable: jspreadsheet.Spreadsheet["updateTable"] = (instance, cell, colIdx, rowIdx, value) => {
        if (!value) {
          value = "";
        }
        value = "" + value;
        const coords = `${colIdx}_${rowIdx}`;
        const currentRow = this.table.current?.getRowData(+rowIdx);
        if (!currentRow || this.noUpdateTable) {
          return;
        }
        const ruleKey = currentRow ? currentRow[this.numberOfRuleCol] : "";
        const currentRule: API_FilenameAssistantRuleResponse = filenameAssistantStore.rules[ruleKey];
        if (value === "" && currentRule && colIdx - this.numberOfRuleCol > currentRule.parts.length) {
          value = "null";
        }

        const pdlFile = this.rowToFile[rowIdx] as FilenameAssistantPdlFile;

        if (
          this.isPDL &&
          currentRule &&
          colIdx === 1 &&
          currentRow[colIdx] !== "*" &&
          !currentRule.validFiletypes.find((t) => t === currentRow[colIdx])
        ) {
          currentRow[1] = currentRule.validFiletypes[0];
          this.noUpdateTable = true;
          this.table.current?.setRowData(rowIdx, currentRow);
          this.noUpdateTable = false;
        }

        if (colIdx === 0 && this.isPDL && cell.parentElement) {
          if (this.state.markedAsDeletedRowIdxs.includes(rowIdx)) {
            cell.parentElement.style.textDecoration = "line-through";
          } else {
            cell.parentElement.style.textDecoration = "";
          }
        }

        if (this.isDocs) {
          if ((colIdx < 2 || value === undefined) && colIdx !== this.numberOfRuleCol) {
            cell.classList.add("jDisabledCell");
          } else {
            cell.classList.remove("jDisabledCell");
          }
        } else {
          if (
            ((colIdx === 2 || value === undefined) && colIdx !== this.numberOfRuleCol) ||
            colIdx > this.numberOfRuleCol + currentRule?.parts?.length ||
            pdlFile?.planDeliveryListFileItems?.length > 0
          ) {
            cell.classList.add("jDisabledCell");
          } else {
            cell.classList.remove("jDisabledCell");
          }
        }

        if (this.isDocs) {
          if (this.state.tableData[rowIdx].style[ruleKey]?.indexOf(coords) !== -1) {
            cell.classList.add("jChangedCell");
          } else {
            cell.classList.remove("jChangedCell");
          }
        } else {
          if (
            (pdlFile?.id !== -1 &&
              (this.state.tableData[rowIdx]?.style[ruleKey]?.indexOf(coords) !== -1 ||
                (colIdx === 0 && currentRow[0] !== "" + pdlFile?.expectedFiles) ||
                (colIdx === 1 && currentRow[1] !== "." + pdlFile?.type && currentRow[1] !== "*") ||
                (colIdx === 3 && currentRow[3] !== pdlFile?.description) ||
                (colIdx === this.numberOfRuleCol && currentRow[this.numberOfRuleCol] !== pdlFile?.ruleName))) ||
            (pdlFile.id < 0 && colIdx > this.numberOfRuleCol && currentRow[colIdx] !== "*")
          ) {
            cell.classList.add("jChangedCell");
          } else {
            cell.classList.remove("jChangedCell", "jValidCell");
          }
        }

        if (this.isDocs) {
          if (colIdx < this.numberOfRuleCol || value === undefined) {
            cell.classList.remove("jValidCell", "jChangedCell");
          } else {
            cell.classList.add("jValidCell");
          }
        } else if (this.isPDL) {
          if (colIdx > this.numberOfRuleCol && value === undefined) {
            cell.classList.remove("jValidCell", "jChangedCell");
          } else if (colIdx > this.numberOfRuleCol) {
            if (value !== "*") {
              cell.classList.add("jValidCell");
            }
          }
        }

        if (value === "freeTextInput" || value === "invalidValue" || value?.endsWith("_invalidValue")) {
          cell.textContent = value;
          cell.innerText = value.replace("_invalidValue", "");
          cell.classList.add("jInvalidCell");
        } else {
          cell.textContent = value;
          cell.classList.remove("jInvalidCell");
        }

        if (this.isPDL && (colIdx === 0 || colIdx === 3)) {
          // col amount and description
          if (currentRow[colIdx] === "" || (colIdx === 0 && isNaN(+currentRow[0]))) {
            cell.classList.add("jInvalidCell");
          } else {
            cell.classList.remove("jInvalidCell");
          }
        }

        if (value === "null") {
          cell.textContent = null;
          cell.innerText = null;
          cell.classList.add("jDisabledCell");
          cell.classList.remove("jInvalidCell", "jValidCell", "jChangedCell");
        }
      };

      cellEditor = () => {
        const { t } = this.props;
        return {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          closeEditor: (cell, save: boolean, x: number, y: number) => {
            if (!save) {
              return;
            }
            if (cell.children[0]) {
              this.handleColorChange(x, y);
              return cell.children[0].value ? cell.children[0].value : "";
            }
            if (
              cell.textContent !== this.state.initialCellValue.split("_")[0] &&
              this.state.initialCellValue !== null
            ) {
              this.handleColorChange(x, y);
            }
            this.handleResetModalData();
            return cell.textContent === this.state.initialCellValue.split("_")[0]
              ? this.state.initialCellValue
              : cell.textContent;
          },
          openEditor: (
            cell: HTMLTableCellElement,
            value: string,
            x: number,
            y: number,
            instance: jspreadsheet.worksheetInstance,
            columnData: jspreadsheet.Column
          ) => {
            cell.classList.add("editor");
            const rowData = this.table.current?.getRowData(+y);

            let typeKey = rowData[0];
            let ruleKey = rowData[2];
            let partIndex = x - 3;
            if (this.isPDL) {
              typeKey = rowData[1];
              ruleKey = rowData[this.numberOfRuleCol];
              partIndex = x - 5;
            }
            setTimeout(() => {
              instance.closeEditor(cell, false);
            }, 50);

            const partTitle = columnData.title as string;

            const rule = filenameAssistantStore.typesMap[typeKey]
              ? filenameAssistantStore.typesMap[typeKey][ruleKey]
              : filenameAssistantStore.typesMap["*"][ruleKey];
            const part = rule.parts[partIndex];

            const partConventions = part.conventions;
            const additonalCharFilter: string[] = [];
            if (rule.format === "BLOCK" && part.position === rule.parts.length && !part.positionRest) {
              additonalCharFilter.push(rule.divider);
            }
            if (partConventions.length > 1) {
              const data = partConventions.map(
                ({ exactLength, length, validValues, format, name, description, conventions }, index) => {
                  // eslint-disable-next-line @typescript-eslint/no-explicit-any
                  const regexp = format === "PATTERN" ? (validValues[0] as any).name : null;
                  // eslint-disable-next-line @typescript-eslint/no-explicit-any
                  const dateFormat = format === "DATUM" ? (validValues[0] as any).name : null;
                  const conventionValue = (value as string).replace("_invalidValue", "").replace("undefined", "");
                  const conventionsData = conventions?.map((v) => ({
                    value: conventionValue,
                    length: v.length,
                    format: v.format,
                    exactLength: v.exactLength,
                    regexp: v.format === "PATTERN" ? v.validValues[0].name : null,
                    dateFormat: v.format === "DATUM" ? v.validValues[0].name : null,
                    validValues: v.validValues,
                    name: v.name,
                    description: v.description,
                    additonalCharFilter,
                  }));
                  return {
                    conventions: conventionsData,
                    value: conventionValue,
                    length,
                    format,
                    exactLength,
                    regexp,
                    dateFormat,
                    validValues,
                    name: name || `${t("Convention")} ${index + 1}`,
                    description,
                    additonalCharFilter,
                  };
                }
              );

              this.setState({
                conventionCase: "OR",
                isMultiConvVisible: true,
                modalTextConvData: data,
                modalTitle: partTitle,
                modalCell: cell,
                initialCellValue: value,
              });
            } else if (partConventions[0].conventions && partConventions[0].multiConv) {
              const data = partConventions[0].conventions.map(
                ({ exactLength, length, validValues, format, name, description }) => {
                  // eslint-disable-next-line @typescript-eslint/no-explicit-any
                  const regexp = format === "PATTERN" ? (validValues[0] as any).name : null;
                  // eslint-disable-next-line @typescript-eslint/no-explicit-any
                  const dateFormat = format === "DATUM" ? (validValues[0] as any).name : null;
                  return {
                    value: (value as string).split("_")[0],
                    length,
                    format,
                    exactLength,
                    regexp,
                    dateFormat,
                    validValues,
                    name,
                    description,
                    additonalCharFilter,
                  };
                }
              );

              this.setState({
                conventionCase: "AND",
                isMultiConvVisible: true,
                modalTextConvData: data,
                modalTitle: partTitle,
                modalCell: cell,
                initialCellValue: value,
              });
            } else if (
              partConventions[0].validValues !== null &&
              partConventions[0].format !== "PATTERN" &&
              partConventions[0].format !== "DATUM"
            ) {
              this.setState({
                isPickerVisible: true,
                modalPickerData: {
                  values: partConventions[0].validValues as { name: string; description: string }[],
                  initialValue: value,
                  activePartConventions: partConventions,
                  additonalCharFilter,
                },
                modalTitle: partTitle,
                modalCell: cell,
                initialCellValue: value,
              });
            } else {
              const { exactLength, length, format, validValues, description, name } = partConventions[0];
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              const isRegexp = format === "PATTERN" ? (validValues[0] as any).name : null;
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              const dateFormat = format === "DATUM" ? (validValues?.[0] as any)?.name : null;

              this.setState({
                isFreeTextVisible: true,
                modalTextConvData: [
                  {
                    value: (value as string).replace("_invalidValue", "").replace("undefined", ""),
                    length,
                    exactLength,
                    regexp: isRegexp,
                    dateFormat,
                    description,
                    name: name,
                    additonalCharFilter,
                  },
                ],
                modalCell: cell,
                modalTitle: partTitle,
                initialCellValue: value,
              });
            }

            this.rerenderModal();
          },
          get: function (cell: HTMLTableCellElement) {
            return cell.textContent;
          },
          setValue: function (cell, value) {
            cell.textContent = value;
          },
        } as unknown as jspreadsheet.Editor;
      };

      parseData = (file?: UploadedTmpFile | API_UploadFile | FilenameAssistantPdlFile) => {
        if (this.props.module === "PDL" && this.props.files.length === 0 && !file) {
          this.setState({ loading: false });
          return;
        }

        const { t } = this.props;
        const data = {};
        const tableData = [];
        const ruleNames: { [key: string]: string[] } = {};
        const fileTypes: string[] = [];
        const fileNames: string[] = [];
        const fileAmounts: string[] = [];
        const fileDescriptions: string[] = [];
        let rulePartsMaxCount = 0;
        const source = file ? [file] : filenameAssistantStore.data;
        const sortedSource = ((): (UploadedTmpFile | API_UploadFile | FilenameAssistantPdlFile)[] => {
          if (this.isPDL) {
            if (this.props.files.length === 0 && file) {
              return [file];
            }
            return this.props.files.map((pdlItem) => ({
              name: pdlItem.plankey,
              type: filetype(pdlItem.plankey),
              ...pdlItem,
            }));
          }
          const arr = [];
          source.forEach((file) => {
            Object.keys(filenameAssistantStore.typesMap).includes(`.${file.type.toLowerCase()}`)
              ? arr.unshift(file)
              : arr.push(file);
          });
          return arr;
        })();

        const setRowStyle = (name: string, type: string, rule?: string) => {
          const stylesMap = {};
          let activeRule = ruleNames[type].indexOf(ruleNames[type].find((v) => v === rule));
          [...ruleNames[type]].forEach((rule) => {
            stylesMap[rule] = [];
          });
          if (this.props.module === "PDL" && activeRule === -1) {
            activeRule = 0;
          }
          tableData.push({ data: [...data[name][ruleNames[type][activeRule]].data], style: stylesMap });
        };
        const fileLoadedInPDL = {};
        for (const typeKey in filenameAssistantStore.typesMap) {
          ruleNames[typeKey] = (() =>
            Object.keys(filenameAssistantStore.typesMap[typeKey]).length === 1
              ? new Array(1).fill(Object.keys(filenameAssistantStore.typesMap[typeKey])[0])
              : [...Object.keys(filenameAssistantStore.typesMap[typeKey])])();

          // eslint-disable-next-line no-loop-func
          sortedSource.forEach((file, fileIdx) => {
            let fileRulenoName = filenameAssistantStore.getFileRuleno(file.name);

            const newFileName = fileName(file.name);
            const activeType = `.${file.type.toLowerCase()}` === typeKey ? typeKey : typeKey === "*" ? "*" : undefined;
            if (this.isDocs) {
              fileTypes.push(`.${file.type.toLowerCase()}`);
              fileNames.push(newFileName);
            }

            const pdlFile = file as FilenameAssistantPdlFile;
            const dataKey = this.isDocs ? newFileName : "" + fileIdx;
            if (
              `.${file.type.toLowerCase()}` === typeKey ||
              (this.isDocs && typeKey === "*" && (!data[newFileName] || data[newFileName].fileId !== file.id)) ||
              (this.isPDL && typeKey === "*")
            ) {
              for (const ruleKey in filenameAssistantStore.typesMap[activeType]) {
                if (this.isPDL) {
                  if (ruleKey !== pdlFile.ruleName || (pdlFile.id > 0 && fileLoadedInPDL[pdlFile.id])) {
                    continue;
                  }
                  fileLoadedInPDL[pdlFile.id] = true;
                  this.rowToFile[fileTypes.length] = file;
                  fileTypes.push(file.type === "*" ? "*" : `.${file.type}`);
                  fileNames.push(newFileName);
                  fileAmounts.push("" + pdlFile.expectedFiles);
                  fileDescriptions.push(file.description);
                  fileRulenoName = ruleKey;
                }
                const currentRule: API_FilenameAssistantRuleResponse = filenameAssistantStore.rules[ruleKey];
                const currentParts = [];
                const currentPartsInfo: { name: string; position: number }[] = [];
                let validValues = null;
                if (currentRule.format === "POSITION") {
                  validValues = [];
                  currentRule.parts.forEach((part, index) => {
                    if (index === currentRule.parts.length - 1) {
                      validValues.push(newFileName.substring(part.position - 1));
                    } else {
                      validValues.push(
                        newFileName.substring(part.position - 1, currentRule.parts[index + 1].position - 1)
                      );
                    }
                  });
                } else {
                  validValues = newFileName.split(currentRule.divider);
                }
                this.checkValues(currentRule, validValues, currentParts, currentPartsInfo);

                if (currentParts.length > rulePartsMaxCount) {
                  rulePartsMaxCount = currentParts.length;
                }
                if (this.isPDL) {
                  for (let i = currentParts.length; i < this.maxRuleLength; i++) {
                    currentParts.push("*");
                  }
                }

                const row = [currentRule.rulename, ...currentParts];

                data[dataKey] = {
                  ...data[dataKey],
                  [ruleKey]: { data: row, metadata: currentPartsInfo },
                  fileId: file.id,
                };
              }
              try {
                setRowStyle(dataKey, activeType, fileRulenoName);
              } catch (e) {
                console.log(e);
              }
            }
          });
        }

        const setDropdownFilters: jspreadsheet.Column["filterOptions"] = (worksheet, cell, x, y, value, options) => {
          if (x === this.numberOfRuleCol) {
            options.source = Object.keys(filenameAssistantStore.rules);
          } else {
            const ruleKey: string = this.table.current?.getCellValueFromCoords(this.numberOfRuleCol, y);
            const rule = filenameAssistantStore.rules[ruleKey];
            options.source = [...rule.validFiletypes];
          }
          return options;
        };

        const columns: jspreadsheet.Column[] = [
          {
            title: t("Category of file"),
            type: customDropdownEditor(),
            source: Object.values(filenameAssistantStore.rules).map((r) => r.rulename),
            width: 201,
            filterOptions: setDropdownFilters,
          },
          ...Array.from({ length: this.maxRuleLength }, (v, k) => k).map((item) => ({
            title: `${t("Part")} ${item + 1}`,
            width: 120,
            type: this.cellEditor(),
          })),
        ];
        if (this.isDocs) {
          columns.unshift(
            { title: t("Type"), width: 100, readOnly: true, type: "text" },
            {
              title: t("Original filename"),
              width: 300,
              readOnly: true,
              type: "html",
            }
          );
          //   tableData[0].data.unshift(fileNames[0]);
          //  tableData[0].data.unshift(fileTypes[0]);
          fileNames.forEach((t, i) => {
            tableData[i]?.data.unshift(t.toString());
          });
          fileTypes.forEach((t, i) => {
            tableData[i]?.data.unshift(t.toString());
          });
        } else {
          columns.unshift(
            { title: t("Amount"), width: 60 },
            {
              title: t("Type"),
              width: 100,
              type: "dropdown",
              source: Object.keys(filenameAssistantStore.typesMap),
              filterOptions: setDropdownFilters,
            },
            { title: t("Preview"), width: 300 },
            { title: t("Description"), width: 300 }
          );
          fileDescriptions.forEach((t, i) => tableData[i].data.unshift(t));
          fileNames.forEach((t, i) => tableData[i].data.unshift(t));
          fileTypes.forEach((t, i) => tableData[i].data.unshift(t));
          fileAmounts.forEach((t, i) => tableData[i].data.unshift(t));
        }
        this.setState(
          {
            data,
            tableData,
            columns,
            fileTypes,
            fileNames,
            fileAmounts,
            fileDescriptions,
            loading: false,
            reinitialize: true,
          },
          () => {
            this.table.current?.emitFakeUpdateTable();
          }
        );
      };

      private checkValues(
        currentRule: API_FilenameAssistantRuleResponse,
        validValues: string[],
        currentParts: string[],
        currentPartsInfo: { name: string; position: number }[]
      ) {
        let position = 1;
        console.log("currentRule.parts", currentRule.parts.slice());
        for (const part of currentRule.parts) {
          let name = null;
          if (this.isPDL && validValues[position - 1] === "*") {
            currentParts.push("*");
            currentPartsInfo.push({ name: "FREE_TEXT", position });
          } else {
            let toCheckValue: string = "";
            const lastPart =
              part.conventions[0].length === -1 &&
              (position === currentRule.parts.length || currentRule.parts.length === 1);
            if (this.isDocs && lastPart) {
              const merged = validValues.slice(position - 1).join(currentRule.divider);
              validValues.splice(position - 1, validValues.length - (position - 1), merged);
              toCheckValue = merged;
            } else {
              toCheckValue = validValues.find((vv, idx) => idx + 1 === position) || "";
            }

            /* this part could be used for autocomplete fields that had exactly one value.
            Miguel told to not implement it yet but leave it uncommented in code, to prob.
            add it later..
            let isCorrect: boolean = ConventionChecker.check(toCheckValue, part.conventions);
            if(!isCorrect && part.conventions[0].format === "STRING" && part.conventions[0].validValues?.length === 1) {
              // tslint:disable-next-line:no-any
              toCheckValue = (part.conventions[0].validValues[0] as any).name;
              isCorrect = true;
            }  
            */
            let isCorrect: boolean = ConventionChecker.check(toCheckValue, part.conventions);
            if (
              isCorrect &&
              lastPart &&
              !part.positionRest &&
              currentRule.format === "BLOCK" &&
              toCheckValue.indexOf(currentRule.divider) > -1
            ) {
              isCorrect = false;
            }
            if (isCorrect && hasBlacklistedFilenameChars(toCheckValue)) {
              isCorrect = false;
            }
            if (part.conventions[0].validValues !== null) {
              name = part.conventions[0].metadata ? part.conventions[0].metadata.name : "INDEX";
              currentParts.push(isCorrect ? toCheckValue : `${toCheckValue}_invalidValue`);
              currentPartsInfo.push({ name, position });
            } else {
              currentParts.push(isCorrect ? toCheckValue : `${toCheckValue}_invalidValue`);
              currentPartsInfo.push({ name: "FREE_TEXT", position });
            }
          }
          position++;
        }
      }

      handleRuleChange = (row: string, newRuleKey: string, oldRuleKey: string) => {
        let newRowData;
        let ruleKey = oldRuleKey;
        const nameKey = this.state.fileNames[row];
        const currentRow = this.table.current?.getRowData(+row);

        if (!newRuleKey) {
          newRowData = this.state.data[nameKey][oldRuleKey].data;
          ruleKey = oldRuleKey;
        } else {
          if (this.isDocs) {
            newRowData = this.state.data[nameKey][newRuleKey].data;
          } else {
            currentRow[this.numberOfRuleCol] = newRuleKey;
          }

          ruleKey = newRuleKey;
        }

        this.setState({
          data: {
            ...this.state.data,
            [nameKey]: {
              ...this.state.data[nameKey],
              active: ruleKey,
            },
          },
        });
        if (this.isDocs) {
          this.table.current?.setRowData(+row, [
            currentRow[0], // skip non-editable "Type" column
            currentRow[1], // skip non-editable "File name" column
            ...Array.from({ length: currentRow.length }, (v, k) => (newRowData.length > k ? newRowData[k] : "null")),
          ]);
        } else {
          const rule = filenameAssistantStore.rules[ruleKey];
          const currentParts = [];
          const currentPartsInfo: { name: string; position: number }[] = [];

          this.checkValues(
            rule,
            currentRow.slice(this.numberOfRuleCol + 1).map((p) => p.replace("_invalidValue", "")),
            currentParts,
            currentPartsInfo
          );
          for (let i = 0; i < currentParts.length; i++) {
            currentRow[this.numberOfRuleCol + 1 + i] = currentParts[i];
          }
          if (!rule.validFiletypes.includes(currentRow[1])) {
            currentRow[1] = rule.validFiletypes[0];
          }
          this.table.current?.setRowData(+row, currentRow);

          this.setPreview(rule, currentRow, row);
        }
      };

      handleColorChange = (x: string | number, y: string | number) => {
        const newTableData = [...this.state.tableData];
        const currentRow = this.table.current?.getRowData(+y);
        const ruleKey = currentRow[this.numberOfRuleCol];
        const coords = `${x}_${y}`;

        if (newTableData[y]?.style[ruleKey]?.indexOf(coords) === -1) {
          newTableData[y].style[ruleKey].push(coords);
        }

        this.setState({
          tableData: newTableData,
        });
      };

      setPreview = (rule, currentRow: string[], rowIdx) => {
        if (!currentRow) {
          notificationError(this.props.t("Error"), this.props.t("Unexpected error occurred. Please contact support."));
          return;
        }
        const preview = currentRow
          .slice(this.numberOfRuleCol + 1, this.numberOfRuleCol + 1 + rule.parts.length)
          .join(rule.divider)
          .replace(/_invalidValue/i, "");
        this.table.current?.setValueFromCoords(2, rowIdx, preview);
      };

      handleEditData = (colIdx: string | number, rowIdx: string | number) => {
        if (!rowIdx) {
          return;
        }
        const currentRow = this.table.current?.getRowData(+rowIdx);
        const nameKey = this.isDocs ? currentRow[1] : rowIdx;
        const ruleKey = currentRow[this.numberOfRuleCol];
        const rule = filenameAssistantStore.rules[ruleKey];

        if (this.isPDL) {
          if (+colIdx > this.numberOfRuleCol) {
            this.setPreview(rule, currentRow, rowIdx);
          }
          return;
        }

        this.setState({
          data: {
            ...this.state.data,
            [nameKey]: {
              ...this.state.data[nameKey],
              [ruleKey]: {
                ...this.state.data[nameKey][ruleKey],
                data: currentRow.slice(this.numberOfRuleCol),
              },
            },
          },
        });
      };

      handleValueChange = (val: string) => {
        const cell = this.state.modalCell;
        cell.textContent = val;
        this.table.current?.closeEditor(cell, true);
      };

      handleResetModalData = () => {
        this.setState({
          conventionCase: null,
          isPickerVisible: false,
          isFreeTextVisible: false,
          isMultiConvVisible: false,
          modalTextConvData: [],
          modalPickerData: { values: [], initialValue: "", activePartConventions: null, additonalCharFilter: [] },
          modalCell: null,
          modalTitle: null,
          initialCellValue: null,
          markedAsDeletedRowIdxs: [],
        });
      };

      handleAbort = () => {
        this.props.hideModal();
      };

      handleReset = async () => {
        this.setState({ reinitialize: false }, () => {
          this.noUpdateTable = true;
          Promise.resolve(this.table.current?.reInitialize()).then(() => {
            this.noUpdateTable = false;
            this.parseData();
          });
        });
      };

      handleNameBuild = () => {
        const nameRuleData = this.table.current?.getTableData() as unknown[][];
        const payload = nameRuleData.map((rule) => {
          const [fileType, fileOldName, ruleKey, ...parts] = rule as string[];
          const divider = filenameAssistantStore.dividers[ruleKey];
          const { metadata } = this.state.data[fileOldName][ruleKey];
          const { fileId } = this.state.data[fileOldName];
          const file = filenameAssistantStore.data.find((file) => file.id === fileId) as UploadedTmpFile;
          const validType = fileType !== "*" ? fileType : `.${file.type}`;
          const name = [];

          parts
            // check this.handleRuleChange -> this.table.current?.setRowData method
            .filter((part) => part && part !== "null")
            .map((part) => (part.includes("invalidValue") ? part.split("_invalidValue")[0] : part))
            .forEach((part, index) => {
              name.splice(metadata[index].position, 0, part);
            });
          return {
            ...file,
            name: `${name.join(divider)}${validType}`,
          };
        });
        this.props.onSubmit(payload);
        this.props.hideModal();
        filenameAssistantStore.clear();
      };

      get invalidParts() {
        return (this.table.current?.getTableData() as unknown[][]).find((rule) =>
          rule.find((part) => part === "invalidValue")
        );
      }
      get maxRuleLength() {
        let max = 0;
        for (const rule in filenameAssistantStore.rules) {
          max = Math.max(max, filenameAssistantStore.rules[rule].parts.length);
        }
        return max;
      }

      get isPDL() {
        return this.props.module === "PDL";
      }
      get isDocs() {
        return this.props.module === "DOCS";
      }

      handleExternChange = () => {
        if (!this.blockExternChange && this.isPDL && this.props.onChange) {
          const values: API_PlanDeliveryListItem[] = [];
          for (const rowIdx in this.rowToFile) {
            if (this.state.markedAsDeletedRowIdxs.includes(+rowIdx)) {
              continue;
            }
            const pdlAssisstFile = this.rowToFile[rowIdx] as FilenameAssistantPdlFile;

            const rowData: string[] = this.table.current?.getRowData(rowIdx);
            if (
              rowData.find((val) => val?.indexOf("_invalidValue") > -1) ||
              rowData[0] === "" ||
              isNaN(+rowData[0]) || // amount
              rowData[3] === "" // description
            ) {
              this.props.onChange(null, null);
              return;
            }
            const fileExt = rowData[1] === "*" ? ".*" : rowData[1];
            if (pdlAssisstFile.id > 0) {
              const pdlFile = this.props.files.find((f) => f.id === pdlAssisstFile.id);

              values.push({
                ...pdlFile,
                plankey: rowData[2] + fileExt,
                expectedFiles: +rowData[0],
                description: rowData[3],
                ruleName: rowData[4],
              });
            } else {
              values.push({
                id: -1,
                plankey: rowData[2] + fileExt,
                expectedFiles: +rowData[0],
                description: rowData[3],
                ruleName: rowData[4],
              });
            }
          }

          this.props.onChange(
            values,
            this.state.markedAsDeletedRowIdxs.map((rowIdx) => this.rowToFile[rowIdx])
          );
        }
      };
      render() {
        const { t } = this.props;
        const ColorInfo = [
          [t("Correct value"), "#92d050"],
          [t("Wrong value"), "#ffb732"],
          [t("Changed value"), "#0080ff"],
        ];
        if (this.state.loading || filenameAssistantStore.state === "LOADING") {
          return <Loader />;
        }

        if (filenameAssistantStore.unknowPDLRules.length > 0) {
          return (
            <div style={{ color: "red", fontWeight: "bold", marginBottom: "15px" }}>
              {t("This package is based on the deleted or renamed rule(s) {{ruleName}}. Please contact the support.", {
                ruleName: filenameAssistantStore.unknowPDLRules.join(", "),
              })}
            </div>
          );
        }
        return (
          <div className="flex" style={{ flexDirection: "column" }}>
            <MultiValuesPickerModal
              isVisible={this.state.isPickerVisible}
              data={this.state.modalPickerData}
              title={this.state.modalTitle}
              onCancel={this.handleResetModalData}
              onChange={this.handleValueChange}
              allowAsterics={this.isPDL}
            />
            <CellValueEditorModal
              isVisible={this.state.isMultiConvVisible || this.state.isFreeTextVisible}
              data={this.state.modalTextConvData}
              multiConventionCase={this.state.conventionCase}
              initialValue={this.state.initialCellValue}
              title={this.state.modalTitle}
              onCancel={this.handleResetModalData}
              onChange={this.handleValueChange}
            />
            {(!this.isPDL || this.props.files.length > 0 || Object.keys(this.rowToFile).length > 0) && (
              <div className="mb-md" style={{ display: "block" }}>
                {ColorInfo.map((item, idx) => {
                  return (
                    <span key={idx}>
                      <div
                        style={{
                          display: "inline-block",
                          border: "1px solid #ccc",
                          marginLeft: 10,
                          width: 50,
                          height: 23,
                          backgroundColor: item[1],
                        }}
                      />
                      <div style={{ display: "inline-block", marginLeft: 3 }}>{item[0]}</div>
                    </span>
                  );
                })}
              </div>
            )}
            {this.isPDL && (
              <Button
                type="primary"
                faIcon="plus"
                disabled={this.props.disabledAdd}
                style={{ width: "fit-content" }}
                onClick={() => {
                  let fileType = "pdf";
                  const rule = filenameAssistantStore.getFirstRule();
                  if (!rule.validFiletypes.includes(".pdf") && rule.validFiletypes.length > 0) {
                    fileType = rule.validFiletypes[0].replace(".", "");
                  }

                  if (Object.keys(this.rowToFile).length === 0) {
                    this.parseData({
                      id: -1,
                      name: Array.from({ length: this.maxRuleLength }, () => "*").join(rule.divider) + "." + fileType,
                      type: fileType,
                      expectedFiles: 1,
                      description: t("Enter a description"),
                      ruleName: rule.rulename,
                    });
                    window.setTimeout(() => {
                      const rowIdx = 0;
                      const data = this.table.current?.getTableData()[0];
                      this.setPreview(rule, data as string[], rowIdx);
                    });
                  } else {
                    const rowIdx = this.table.current?.getTableData().length;

                    const data = [
                      "1",
                      "." + fileType,
                      Array.from({ length: this.maxRuleLength }, () => "*").join(rule.divider),
                      t("Enter a description"),
                      rule.rulename,
                      ...Array.from({ length: rule.parts.length }, () => "*"),
                    ];
                    this.rowToFile[rowIdx] = { id: -1 };
                    if (fileType === "*") {
                      data[1] = "*";
                    }

                    this.table.current?.addRow(data);
                    this.setPreview(rule, data, rowIdx);
                  }
                }}
              >
                {t("Plans")}
              </Button>
            )}
            <div
              className={classNames("relative", this.props.className)}
              style={{ flexGrow: 1, overflow: "hidden", height: "300px" }}
            >
              {!this.state.reinitialize && <Loader className="coverAbsolute" />}
              {!(this.isPDL && this.props.files.length === 0 && Object.keys(this.rowToFile).length === 0) && (
                <Jspreadsheet
                  ref={this.table}
                  loadingSpin={this.state.loading}
                  worksheets={[
                    {
                      tableOverflow: true,
                      data: this.state.tableData.map((item) => item.data),
                      columns: this.state.columns,
                      freezeColumns: this.numberOfRuleCol,
                      rowResize: false,
                      columnResize: false,
                      columnSorting: false,
                      selectionCopy: false,
                      tableHeight: "300px",
                      rowDrag: false,
                      allowInsertRow: true,
                      allowManualInsertRow: false,
                    },
                  ]}
                  stretchHeight={true}
                  className="jLayout"
                  updateTable={this.updateTable}
                  onafterchanges={(el, records) => {
                    if (+records[0].x === this.numberOfRuleCol) {
                      this.handleRuleChange(records[0].y, records[0].value, records[0].oldValue);
                    } else {
                      this.handleEditData(records[0].x, records[0].y);
                    }
                    this.handleExternChange();
                  }}
                  onselection={this.onSelection}
                  contextMenu={this.contextMenu}
                />
              )}
            </div>
            {this.isDocs && (
              <ButtonsRow justify="space-between" style={{ marginTop: 10 }}>
                <Button onClick={this.handleAbort}>{t("Abort")}</Button>
                <div>
                  <Button onClick={this.handleReset} style={{ marginRight: 10 }}>
                    {t("Reset all")}
                  </Button>
                  <Button type="primary" onClick={this.handleNameBuild}>
                    {t("Confirm")}
                  </Button>
                </div>
              </ButtonsRow>
            )}
          </div>
        );
      }
    }
  )
);
